import type { FormData } from '../../types'

import { parseISO } from 'date-fns/esm'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectCompanies } from '@/features/domain/company'
import { selectDepots } from '@/features/domain/depot'
import { updateOrders as updateOrdersAction } from '@/features/domain/order'
import { selectRecurrences } from '@/features/domain/routeplan'
import { selectStartOfToday } from '@/features/domain/user'
import { selectLicensingLimits } from '@/features/domain/user'
import { useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'

import { updateTagsWarning } from '../../utils/updateTagsWarning'

import { computeCompanyId } from './utils/computeCompanyId'
import { computeEligibility } from './utils/computeEligibility'
import { computeEmail } from './utils/computeEmail'
import { computeForceVehicleId } from './utils/computeForceVehicleId'
import { computeLoads } from './utils/computeLoads'
import { computeLocation } from './utils/computeLocation'
import { computeNotes } from './utils/computeNotes'
import { computePhone } from './utils/computePhone'
import { computePriority } from './utils/computePriority'
import { computeRecurrenceSettings } from './utils/computeRecurrenceSettings'
import { computeServiceTimeSec } from './utils/computeServiceTimeSec'
import { computeTags } from './utils/computeTags'
import { computeTimeWindows } from './utils/computeTimeWindows'
import { computeType } from './utils/computeType'

export function useFormData(orders: uui.domain.client.rm.ExtendedOrderStep[]) {
  const depots = useSelector(selectDepots)
  const dispatch = useAppDispatch()
  const toast = useNotification()
  const maxTagsCount = useSelector(selectLicensingLimits).maxTags
  const companies = useSelector(selectCompanies)
  const todayAsString = useSelector(selectStartOfToday)
  const today = useMemo(() => parseISO(todayAsString), [todayAsString])
  const recurrences = useSelector(selectRecurrences)

  const initialValues = useMemo<FormData>(() => {
    const values = {
      eligibility: computeEligibility(orders),
      email: computeEmail(orders),
      companyId: computeCompanyId(orders, companies),
      forceVehicleId: computeForceVehicleId(orders),
      loads: computeLoads(orders),
      location: computeLocation(orders, depots),
      notes: computeNotes(orders),
      phone: computePhone(orders),
      priority: computePriority(orders),
      serviceTimeSec: computeServiceTimeSec(orders),
      tagsIn: computeTags(orders, 'in', maxTagsCount),
      tagsOut: computeTags(orders, 'out', maxTagsCount),
      timeWindows: computeTimeWindows(orders),
      type: computeType(orders),
      recurrenceSettings: computeRecurrenceSettings(orders, recurrences, today),
    }

    updateTagsWarning(values.tagsIn, values.tagsOut)

    return values
  }, [orders, depots, maxTagsCount, companies])

  const updateOrders = useCallback(
    async (data: uui.domain.ui.forms.BulkOrderUpdate) => {
      try {
        const thunkResult = await dispatch(updateOrdersAction(data))

        if (updateOrdersAction.rejected.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        return true
      } catch (e) {
        if (e instanceof Error) {
          toast.error(e.message)
        }
      }

      return false
    },
    [dispatch, toast],
  )

  return [initialValues, updateOrders] as const
}
